/* eslint-disable max-lines-per-function */
import React, { useContext, useState } from 'react';
import { ZCDSelect } from '@zencity/common-ui';
import { LoaderMask } from 'components/LoaderMask/LoaderMask';
import { WidgetGeneratorContext } from 'screens/WidgetGenerator/contexts/WidgetGeneratorContext';
import { AspectValue, DemographicGeographicType } from 'screens/WidgetGenerator/utils/misc';
import { logger } from 'utils/logger';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'customHooks/hooks';
import { Nullable } from 'types/misc';
import { FieldTitle } from 'screens/WidgetGenerator/components/FieldTitle/FieldTitle';
import { CROSS_QUESTION_TYPES } from 'screens/WidgetGenerator/widgets/CrossTabs/utils';
import { SelectOption } from '@zencity/common-ui/lib/ZCD/ZCDFilter/types';
import { getGenericQuestionsData } from 'screens/WidgetGenerator/widgetAdditionalFields/utilsFunctionsAdditionalFields/GenericQuestionSelection/utils';
import { ReportBuilderFilterProps } from '../../constants';

export enum CrossTabsFiltersType {
  BASE_QUESTION = 'baseQuestion',
  CROSS_QUESTIONS = 'crossQuestions',
}

interface CrossTabsFiltersData {
  [CrossTabsFiltersType.BASE_QUESTION]: SelectOption[];
  [CrossTabsFiltersType.CROSS_QUESTIONS]: SelectOption[];
}

export interface CrossTabsFiltersSelectedData {
  [CrossTabsFiltersType.BASE_QUESTION]: Nullable<SelectOption>;
  [CrossTabsFiltersType.CROSS_QUESTIONS]: SelectOption[];
}

export const EMPTY_CROSS_TABS_FILTERS_DATA: CrossTabsFiltersData = {
  [CrossTabsFiltersType.BASE_QUESTION]: [],
  [CrossTabsFiltersType.CROSS_QUESTIONS]: [],
};

export const EMPTY_CROSS_TABS_FILTERS_VALUE: CrossTabsFiltersSelectedData = {
  [CrossTabsFiltersType.BASE_QUESTION]: null,
  [CrossTabsFiltersType.CROSS_QUESTIONS]: [],
};

export const CrossTabsFilters: React.FC<ReportBuilderFilterProps> = (props: ReportBuilderFilterProps) => {
  const { value: crossTabsFilterValue, onChange } = props;
  const filterValue = crossTabsFilterValue as CrossTabsFiltersSelectedData;
  const { client, cycles, selectedDateRange, selectedSurveyGroup } = useContext(WidgetGeneratorContext);
  const { t: translate } = useTranslation();

  const [selectedFiltersData, setSelectedFiltersData] = useState({
    [CrossTabsFiltersType.BASE_QUESTION]: filterValue?.[CrossTabsFiltersType.BASE_QUESTION],
    [CrossTabsFiltersType.CROSS_QUESTIONS]: filterValue?.[CrossTabsFiltersType.CROSS_QUESTIONS],
  });

  const fetchCrossTabsFiltersData = async () => {
    const mappedBaseGenericQuestions = await getGenericQuestionsData({
      selectedDateRange,
      selectedSurveyGroup,
      zcClientId: client.zc_client_id,
    });

    const mappedCrossGenericQuestions = await getGenericQuestionsData({
      aspect: AspectValue.DEMOGRAPHICS,
      selectedDateRange,
      selectedSurveyGroup,
      questionTypesToInclude: CROSS_QUESTION_TYPES,
      zcClientId: client.zc_client_id,
    });
    const extendedCrossGenericQuestions = [...mappedCrossGenericQuestions];

    // Add DemographicGeographicType to the list of cross questions
    Object.entries(DemographicGeographicType).forEach(([demographicKey, demographicLabel]) => {
      extendedCrossGenericQuestions.push({ value: demographicKey, label: demographicLabel });
    });

    return {
      [CrossTabsFiltersType.BASE_QUESTION]: mappedBaseGenericQuestions,
      [CrossTabsFiltersType.CROSS_QUESTIONS]: extendedCrossGenericQuestions,
    };
  };

  const {
    fetchedData: crossTabsFiltersData = EMPTY_CROSS_TABS_FILTERS_DATA,
    isLoading,
    error,
  } = useAsyncEffect(fetchCrossTabsFiltersData, [client, cycles, selectedDateRange]);
  if (error) {
    logger.error(error);
  }

  const handleBaseQuestionChange = (newValue: unknown) => {
    const newSelectedQuestions = newValue as SelectOption;

    setSelectedFiltersData({
      ...selectedFiltersData,
      [CrossTabsFiltersType.BASE_QUESTION]: newSelectedQuestions,
    });
    onChange({ ...filterValue, [CrossTabsFiltersType.BASE_QUESTION]: newValue });
  };

  const handleCrossQuestionChange = (newValue: unknown) => {
    const newSelectedQuestions = newValue as SelectOption[];
    setSelectedFiltersData({ ...selectedFiltersData, [CrossTabsFiltersType.CROSS_QUESTIONS]: newSelectedQuestions });
    onChange({
      ...filterValue,
      [CrossTabsFiltersType.CROSS_QUESTIONS]: newSelectedQuestions,
    });
  };

  return (
    <LoaderMask isLoading={isLoading}>
      <FieldTitle title={translate('widgetGenerator.filters.crossTabs.selectBaseQuestion')} />
      <ZCDSelect
        placeholder={translate('widgetGenerator.filters.crossTabs.selectBaseQuestion')}
        options={crossTabsFiltersData[CrossTabsFiltersType.BASE_QUESTION]}
        onChange={handleBaseQuestionChange}
        value={selectedFiltersData[CrossTabsFiltersType.BASE_QUESTION]}
      />
      <FieldTitle title={translate('widgetGenerator.filters.crossTabs.selectCrossQuestions')} />
      <ZCDSelect
        isMulti
        placeholder={translate('widgetGenerator.filters.crossTabs.selectCrossQuestions')}
        options={crossTabsFiltersData[CrossTabsFiltersType.CROSS_QUESTIONS]}
        onChange={handleCrossQuestionChange}
        value={selectedFiltersData[CrossTabsFiltersType.CROSS_QUESTIONS]}
      />
    </LoaderMask>
  );
};
